import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import api, {
  ACCESS_ROUTE_EVENT,
  AUTH_STATE_EVENT,
  captureAuthStateSnapshot,
  clearAuthTokens,
  fetchAndStoreAccessRouteDecision,
  getApiRuntimeDiagnostics,
  getAccessRouteBlockedMessage,
  getStoredAccessRouteDecision,
  hasAuthSessionHint,
  isAuthStateSnapshotCurrent,
  isTransientNetworkError,
  setCurrentUser,
} from "./api";
import { isLocalHostname } from "./lib/hostname";
import { BrandingProvider } from "./context/BrandingContext";
import { PrintPreferencesProvider } from "./context/PrintPreferencesContext";
import { PrivacyProvider } from "./context/PrivacyContext";
import { LanguageProvider } from "./context/LanguageContext";
import "./i18n";
import { initPushNotifications } from "./lib/pushNotifications";
import { Toaster, toast } from "./components/ui/sonner";
import { EyeOff } from "lucide-react";
import "./App.css";
import { isDemoMode } from "./services/mode";
import DemoBanner from "./components/DemoBanner";
import DemoWatermark from "./components/DemoWatermark";

import Login from "./pages/Login";
import RuntimeDiagnostics from "./pages/RuntimeDiagnostics";
import VaultUnlock from "./pages/VaultUnlock";
const lazyNamed = (importer, fallbackName) =>
  lazy(() =>
    importer().then((mod) => ({
      default: mod?.default ?? (fallbackName ? mod?.[fallbackName] : undefined),
    }))
  );

const Dashboard = lazyNamed(() => import("./pages/Dashboard"), "Dashboard");
const Customers = lazyNamed(() => import("./pages/Customers"), "Customers");
const Invoices = lazyNamed(() => import("./pages/Invoices"), "Invoices");
const InvoiceView = lazyNamed(() => import("./pages/InvoiceView"), "InvoiceView");
const GoldRateSettings = lazyNamed(() => import("./pages/GoldRateSettings"), "GoldRateSettings");
const ItemMaster = lazyNamed(() => import("./pages/ItemMaster"), "ItemMaster");
const Reports = lazyNamed(() => import("./pages/Reports"), "Reports");
const StockDetails = lazyNamed(() => import("./pages/StockDetails"), "StockDetails");
const CustomerBalanceReports = lazyNamed(() => import("./pages/CustomerBalanceReports"), "CustomerBalanceReports");
const Repairs = lazyNamed(() => import("./pages/Repairs"), "Repairs");
const KarigarManagement = lazyNamed(() => import("./pages/KarigarManagement"), "KarigarManagement");
const Purchases = lazyNamed(() => import("./pages/Purchases"), "Purchases");
const PurchaseView = lazyNamed(() => import("./pages/PurchaseView"), "PurchaseView");
const Expenses = lazyNamed(() => import("./pages/Expenses"), "Expenses");
const PurchaseReturns = lazyNamed(() => import("./pages/PurchaseReturns"), "PurchaseReturns");
const Consignment = lazyNamed(() => import("./pages/Consignment"), "Consignment");
const CustomerBalanceManagement = lazyNamed(() => import("./pages/CustomerBalanceManagement"), "CustomerBalanceManagement");
const SupplierBalance = lazyNamed(() => import("./pages/SupplierBalance"), "SupplierBalance");
const Settings = lazyNamed(() => import("./pages/Settings"), "Settings");
const CustomerRelation = lazyNamed(() => import("./pages/CustomerRelation"), "CustomerRelation");
const GlobalAssistant = lazyNamed(() => import("./components/GlobalAssistant"), "GlobalAssistant");
const StorageUnits = lazyNamed(() => import("./pages/StorageUnits"), "StorageUnits");
const BulkInventory = lazyNamed(() => import("./pages/BulkInventory"), "BulkInventory");
const StockVerification = lazyNamed(() => import("./pages/StockVerification"), "StockVerification");

const DEMO_MODE = isDemoMode();

/* APPLY THEME EARLY */
const savedTheme = localStorage.getItem("mmj_theme");
document.documentElement.classList.toggle("dark", savedTheme === "dark");
const WEEKLY_PENDING_REMINDER_KEY = "mmj_pending_customer_reminder_last_date";
const SESSION_ACTIVITY_SYNC_KEY = "mmj_last_activity_at";
const SESSION_TIMEOUT_DEBUG_KEY = "mmj_session_timeout_debug";
const SESSION_TIMEOUT_CHANNEL = "mmj-session-timeout";

const pushClientErrorLog = (entry) => {
  if (typeof window === "undefined") return;
  const payload = {
    ts: new Date().toISOString(),
    scope: "app",
    level: "error",
    ...entry,
  };

  try {
    const key = "mmj_client_error_log";
    const existing = JSON.parse(window.localStorage.getItem(key) || "[]");
    const next = [...existing.slice(-99), payload];
    window.localStorage.setItem(key, JSON.stringify(next));
    window.__MMJClientErrorLog = next;
  } catch {
    // ignore local log storage failures
  }

  try {
    api.post("/client-log", payload).catch(() => {});
  } catch {
    // ignore backend client-log failures
  }
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    pushClientErrorLog({
      stage: "app-render-boundary",
      level: "error",
      message: error?.message || String(error || "Unknown app render error"),
      stack: error?.stack || "",
      componentStack: info?.componentStack || "",
      route:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.hash || ""}`
          : "",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-slate-950 px-6 text-center text-white">
          <div className="max-w-md rounded-2xl border border-emerald-500/30 bg-slate-900/90 p-6 shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_16px_48px_rgba(0,0,0,0.38)]">
            <h1 className="text-lg font-semibold text-emerald-300">This page hit an app error</h1>
            <p className="mt-2 text-sm text-slate-300">
              The issue has been logged to the launcher. Please refresh the page and try again.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function AuthPendingScreen() {
  return (
    <div className="w-screen h-screen flex items-center justify-center text-xl">
      Loading...
    </div>
  );
}

function canAccessSection(user, sectionPath) {
  if (!sectionPath) return true;
  const sectionFallbacks = {
    "/storage": ["/items"],
    "/bulk-inventory": ["/items"],
    "/verification": ["/items"],
  };
  const sectionPermissions = user?.section_permissions && typeof user.section_permissions === "object"
    ? user.section_permissions
    : {};
  const allowedFromPermissions = Object.keys(sectionPermissions);
  const allowedSections = allowedFromPermissions.length > 0
    ? allowedFromPermissions
    : (Array.isArray(user?.allowed_sections) ? user.allowed_sections : []);
  if (allowedSections.length === 0) return true;
  const candidateSections = [sectionPath, ...(sectionFallbacks[sectionPath] || [])];
  return candidateSections.some((candidate) => allowedSections.includes(candidate));
}

function SectionRoute({ children, user, onLogout, sectionPath, loadingUser }) {
  if (loadingUser) return <AuthPendingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessSection(user, sectionPath)) return <Navigate to="/" replace />;
  return React.cloneElement(children, { user, onLogout });
}

function normalizeSessionTimeoutMinutes(value) {
  if (value === 0 || value === "0") return 0;
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "never" || raw === "none") return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 5;
  if (parsed <= 0) return 0;
  return Math.max(1, Math.min(1440, Math.round(parsed)));
}

function App() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  // Demo reset bumps this so the routed view remounts and refetches in-SPA
  // (no full page reload). Inert outside demo mode.
  const [demoEpoch, setDemoEpoch] = useState(0);
  const [backendMismatch, setBackendMismatch] = useState(
    () => typeof window !== "undefined" && !!window.__MMJBackendMismatch
  );
  const [remoteServerOffline, setRemoteServerOffline] = useState(false);
  const [runtimeOwnership, setRuntimeOwnership] = useState(null);
  const [loadingRuntimeOwnership, setLoadingRuntimeOwnership] = useState(
    () => typeof window !== "undefined" && Boolean(window.JEWEL_FLOW_ENV?.isElectron)
  );
  const [primaryVaultPassword, setPrimaryVaultPassword] = useState("");
  const [claimingPrimary, setClaimingPrimary] = useState(false);
  const isElectronRuntime =
    typeof window !== "undefined" && Boolean(window.JEWEL_FLOW_ENV?.isElectron);

  useEffect(() => {
    if (!DEMO_MODE) return undefined;
    const onDemoReset = () => setDemoEpoch((e) => e + 1);
    window.addEventListener("jf-demo-reset", onDemoReset);
    return () => window.removeEventListener("jf-demo-reset", onDemoReset);
  }, []);

  useEffect(() => {
    const preload = () => {
      import("./pages/Dashboard");
      import("./pages/Invoices");
      import("./pages/Purchases");
      import("./pages/Customers");
      import("./pages/CustomerBalanceManagement");
      import("./pages/SupplierBalance");
      import("./components/GlobalAssistant");
    };
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(preload, { timeout: 1500 });
      return () => window.cancelIdleCallback?.(id);
    }
    const timer = setTimeout(preload, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isElectronRuntime || !window.electronAPI?.getRuntimeOwnershipStatus) {
      setLoadingRuntimeOwnership(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await window.electronAPI.getRuntimeOwnershipStatus();
        if (cancelled) return;
        setRuntimeOwnership(res?.ownership || null);
      } catch {
        if (cancelled) return;
        setRuntimeOwnership(null);
      } finally {
        if (!cancelled) setLoadingRuntimeOwnership(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isElectronRuntime]);


  const [loadingUser, setLoadingUser] = useState(true);
  const insecureLaunchWarnedRef = useRef(false);
  const [showScreenShareBlocked, setShowScreenShareBlocked] = useState(false);
  const [captureEventActive, setCaptureEventActive] = useState(false);
  const [captureHoldActive, setCaptureHoldActive] = useState(false);
  const [securityPolicy, setSecurityPolicy] = useState({
    allow_screenshots: false,
    allow_screen_share: false,
    session_timeout_minutes: 5,
  });
  const [watermarkNow, setWatermarkNow] = useState(() => new Date());
  const lastActivityAtRef = useRef(Date.now());
  const lastActivitySyncAtRef = useRef(0);
  const activityChannelRef = useRef(null);
  const authSessionCycleRef = useRef(0);
  const sessionExpiryRedirectRef = useRef(false);
  const lastSecurityLogRef = useRef({
    screenshot_blocked: 0,
    screen_share_blocked: 0,
    screen_share_started: 0,
  });
  const isRemoteTenantShell = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    if (window.JEWEL_FLOW_ENV?.isElectron) return false;
    const host = String(window.location.hostname || "").toLowerCase().trim();
    if (!host) return false;
    return !isLocalHostname(host) && host !== "::1";
  }, []);
  const logSessionTimeoutDebug = useCallback(
    (eventType, extra = {}) => {
      const timeoutMinutes = normalizeSessionTimeoutMinutes(securityPolicy.session_timeout_minutes);
      const currentTime = Date.now();
      const lastActivity = lastActivityAtRef.current;
      const timeRemainingMs =
        timeoutMinutes > 0 ? Math.max(0, timeoutMinutes * 60 * 1000 - (currentTime - lastActivity)) : null;
      const payload = {
        eventType,
        timeoutMinutes,
        timeoutMode: timeoutMinutes === 0 ? "never" : "inactivity",
        lastActivityAt: new Date(lastActivity).toISOString(),
        currentTime: new Date(currentTime).toISOString(),
        timeRemainingMs,
        timeRemainingMinutes: timeRemainingMs == null ? null : Number((timeRemainingMs / 60000).toFixed(2)),
        ...extra,
      };
      try {
        window.__MMJSessionTimeoutDebug = payload;
        window.localStorage.setItem(SESSION_TIMEOUT_DEBUG_KEY, JSON.stringify(payload));
      } catch {
        // ignore debug storage failures
      }
      console.debug("[session-timeout]", payload);
    },
    [securityPolicy.session_timeout_minutes]
  );
  const markActivity = useCallback((reason = "interaction", options = {}) => {
    const now = Date.now();
    lastActivityAtRef.current = now;
    if (options.broadcast === false) return;
    if (now - lastActivitySyncAtRef.current < 1000 && reason === "mousemove") return;
    lastActivitySyncAtRef.current = now;
    try {
      window.localStorage.setItem(SESSION_ACTIVITY_SYNC_KEY, String(now));
    } catch {
      // ignore storage sync failures
    }
    try {
      activityChannelRef.current?.postMessage({ type: "activity", at: now, reason });
    } catch {
      // ignore broadcast failures
    }
  }, []);
  const allowScreenshots = Boolean(securityPolicy.allow_screenshots);
  const screenshotProtectionEnabled = !allowScreenshots;
  const logSecurityActivity = useCallback((eventType, details = "") => {
    if (!user) return;
    const nowMs = Date.now();
    const lastMs = Number(lastSecurityLogRef.current?.[eventType] || 0);
    if (nowMs - lastMs < 2000) return;
    lastSecurityLogRef.current[eventType] = nowMs;
    api
      .post("/auth/security-activity/log", {
        event_type: eventType,
        page: `${window.location.pathname}${window.location.hash || ""}`,
        details,
      })
      .catch(() => {});
  }, [user]);

  const forceReloginRef = useRef(async () => {});
  forceReloginRef.current = async (reason = "session_expired", message = "Session expired. Please log in again.") => {
    if (sessionExpiryRedirectRef.current) return;
    sessionExpiryRedirectRef.current = true;
    try {
      logSessionTimeoutDebug("logout", { logoutReason: reason, message });
      if (message) toast.info(message);
      await api.post("/auth/logout");
    } catch {
      // Continue local logout even when the session is already invalid.
    }
    clearAuthTokens();
    localStorage.removeItem("mmj_clear_mode_session");
    setUser(null);
    setCurrentUser(null);
    window.location.href = "/#/login";
    setTimeout(() => {
      sessionExpiryRedirectRef.current = false;
    }, 1500);
  };
  const triggerCaptureShield = useCallback(() => {
    if (!screenshotProtectionEnabled) return;
    logSecurityActivity("screenshot_blocked");
    setCaptureEventActive(true);
    setWatermarkNow(new Date());
    setTimeout(() => setCaptureEventActive(false), 2200);
  }, [logSecurityActivity, screenshotProtectionEnabled]);
  useEffect(() => {
    const slug = String(user?.business_id || "").trim();
    if (!slug) return;
    if (typeof window === "undefined") return;
    if (!window.electronAPI?.setActiveBusinessSlug) return;
    window.electronAPI.setActiveBusinessSlug(slug).catch(() => {});
  }, [user?.business_id]);

  useEffect(() => {
    const handler = (e) => {
      setBackendMismatch(true);
      console.error("[JewelFlow] Backend mismatch detected:", e?.detail);
    };
    window.addEventListener("mmj-backend-mismatch", handler);
    return () => window.removeEventListener("mmj-backend-mismatch", handler);
  }, []);

  useEffect(() => {
    const handleWindowError = (event) => {
      pushClientErrorLog({
        stage: "window-error",
        level: "error",
        message: event?.message || event?.error?.message || "Unhandled window error",
        stack: event?.error?.stack || "",
        source: event?.filename || "",
        line: event?.lineno || 0,
        column: event?.colno || 0,
        route: `${window.location.pathname}${window.location.hash || ""}`,
      });
    };

    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      pushClientErrorLog({
        stage: "unhandled-rejection",
        level: "error",
        message:
          reason?.message ||
          (typeof reason === "string" ? reason : "Unhandled promise rejection"),
        stack: reason?.stack || "",
        route: `${window.location.pathname}${window.location.hash || ""}`,
      });
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    if (!window.JEWEL_FLOW_ENV?.isElectron) return;

    const restoreFocus = () => {
      setTimeout(() => {
        if (window.electronAPI?.forceFocus) {
          window.electronAPI.forceFocus();
        } else if (typeof window.focus === "function") {
          window.focus();
        }
      }, 0);
    };

    const originalConfirm = window.confirm.bind(window);
    const originalAlert = window.alert.bind(window);
    const originalPrompt = window.prompt.bind(window);

    window.confirm = (...args) => {
      const result = originalConfirm(...args);
      restoreFocus();
      return result;
    };

    window.alert = (...args) => {
      originalAlert(...args);
      restoreFocus();
    };

    window.prompt = (...args) => {
      const result = originalPrompt(...args);
      restoreFocus();
      return result;
    };

    return () => {
      window.confirm = originalConfirm;
      window.alert = originalAlert;
      window.prompt = originalPrompt;
    };
  }, []);

  useEffect(() => {
    const handleAuthStateChanged = (event) => {
      authSessionCycleRef.current += 1;
      if (event?.detail?.authenticated) {
        const now = Date.now();
        lastActivityAtRef.current = now;
        try {
          window.localStorage.setItem(SESSION_ACTIVITY_SYNC_KEY, String(now));
        } catch {
          // ignore storage sync failures
        }
      }
      sessionExpiryRedirectRef.current = false;
      setLoadingUser(false);
    };
    window.addEventListener(AUTH_STATE_EVENT, handleAuthStateChanged);
    return () => window.removeEventListener(AUTH_STATE_EVENT, handleAuthStateChanged);
  }, []);

  /* Validate auth session (cookie-first) */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (insecureLaunchWarnedRef.current) return;
    const runtime = getApiRuntimeDiagnostics();
    const isHttpPage = String(window.location.protocol || "").toLowerCase() === "http:";
    const secureBackend = String(runtime?.selectedBackendUrl || runtime?.selectedApiBase || "").startsWith("https://");
    if (!isHttpPage || !secureBackend) return;
    insecureLaunchWarnedRef.current = true;
    const secureCurrentUrl = `https://${window.location.host}${window.location.pathname}${window.location.hash || ""}`;
    const secureBuiltUrl = `https://localhost:8004${window.location.pathname}${window.location.hash || ""}`;
    toast.error(
      `This browser session is running on HTTP while the backend is secure. Use ${secureCurrentUrl} for secure dev mode or ${secureBuiltUrl} for the built HTTPS frontend.`,
      { duration: 15000 }
    );
  }, []);

  useEffect(() => {
    const syncAccessRoute = () => {
      const decision = getStoredAccessRouteDecision();
      if (decision?.blocked) {
        setUser(null);
        setCurrentUser(null);
      }
    };
    syncAccessRoute();
    window.addEventListener(ACCESS_ROUTE_EVENT, syncAccessRoute);
    return () => window.removeEventListener(ACCESS_ROUTE_EVENT, syncAccessRoute);
  }, []);

  useEffect(() => {
    if (!isRemoteTenantShell || backendMismatch || user) {
      setRemoteServerOffline(false);
      return;
    }

    let cancelled = false;
    const probeRemoteBackend = async () => {
      try {
        await api.get("/health", {
          timeout: 4000,
          skipDedupe: true,
          __suppressAuthLogout: true,
          __skipAccessRouteRefresh: true,
        });
        if (!cancelled) setRemoteServerOffline(false);
      } catch (err) {
        const status = Number(err?.response?.status || 0);
        const backendReachable = status === 401 || status === 403 || status === 404;
        const offlineLike =
          !backendReachable &&
          (!status || status === 502 || status === 503 || status === 504 || isTransientNetworkError(err));
        if (!cancelled) setRemoteServerOffline(Boolean(offlineLike));
      }
    };

    probeRemoteBackend();
    const handleOnline = () => {
      probeRemoteBackend();
    };
    window.addEventListener("online", handleOnline);
    return () => {
      cancelled = true;
      window.removeEventListener("online", handleOnline);
    };
  }, [backendMismatch, isRemoteTenantShell, user]);

  useEffect(() => {
    if (isElectronRuntime) {
      if (loadingRuntimeOwnership) return;
      if (runtimeOwnership && !runtimeOwnership.allow_local_hosting) {
        setLoadingUser(false);
        return;
      }
    }
    const authStateSnapshot = captureAuthStateSnapshot();
    try {
      const onLoginRoute = String(window.location.hash || "").startsWith("#/login");
      const hasLocalAuthHint = hasAuthSessionHint();
      if (onLoginRoute && !hasLocalAuthHint) {
        setLoadingUser(false);
        return;
      }
    } catch {
      // Fall through and run auth probe.
    }

    const authProbeTimeoutMs = String(window.location.hash || "").startsWith("#/login")
      ? 2500
      : 10000;
    (async () => {
      try {
        const onLoginRoute = String(window.location.hash || "").startsWith("#/login");

        if (isElectronRuntime) {
          try {
            const statusRes = await api.get("/local-security/status", { timeout: 2000, __skipProactiveRefresh: true });
            if (statusRes.data.configured && !statusRes.data.unlocked) {
              if (statusRes.data.has_remembered_vault && !onLoginRoute) {
                try {
                  await api.post("/local-security/unlock-saved", {}, { timeout: 5000 });
                } catch {
                  window.location.hash = "#/vault-unlock?mode=general";
                  setLoadingUser(false);
                  return;
                }
              } else if (!onLoginRoute && !String(window.location.hash || "").includes("/vault-unlock")) {
                window.location.hash = "#/vault-unlock?mode=general";
                setLoadingUser(false);
                return;
              }
            }
          } catch {
            // Fall through
          }
        }

        if (hasAuthSessionHint() && !onLoginRoute) {
          const accessDecision = await fetchAndStoreAccessRouteDecision({
            suppressLogoutOnUnauthorized: true,
            timeout: Math.min(authProbeTimeoutMs, 4000),
          });
          if (!isAuthStateSnapshotCurrent(authStateSnapshot)) return;
          if (accessDecision?.blocked) {
            clearAuthTokens();
            setUser(null);
            setCurrentUser(null);
            toast.error(getAccessRouteBlockedMessage(accessDecision));
            return;
          }
        }
        const res = await api.get("/auth/me", { timeout: authProbeTimeoutMs });
        if (!isAuthStateSnapshotCurrent(authStateSnapshot)) return;
        setUser(res.data);
        setCurrentUser(res.data);
        setRemoteServerOffline(false);

        const theme = res.data.theme || "light";
        localStorage.setItem("mmj_theme", theme);
        document.documentElement.classList.toggle("dark", theme === "dark");
      } catch (err) {
        if (!isAuthStateSnapshotCurrent(authStateSnapshot)) return;
        const status = Number(err?.response?.status || 0);
        if (status === 401 || status === 403) {
          clearAuthTokens();
          setRemoteServerOffline(false);
        } else if (
          isRemoteTenantShell &&
          (!status || status === 502 || status === 503 || status === 504 || isTransientNetworkError(err))
        ) {
          setRemoteServerOffline(true);
        }
        setUser(null);
        setCurrentUser(null);
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [isElectronRuntime, isRemoteTenantShell, loadingRuntimeOwnership, runtimeOwnership]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const accessDecision = await fetchAndStoreAccessRouteDecision({ suppressLogoutOnUnauthorized: true });
        if (cancelled) return;
        if (accessDecision?.blocked) {
          clearAuthTokens();
          setUser(null);
          setCurrentUser(null);
          toast.error(getAccessRouteBlockedMessage(accessDecision));
        }
      } catch {
        // Keep the existing session flow intact when the advisory access check is temporarily unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const maybeShowPendingReminder = () => {
      const now = new Date();
      const isSaturday = now.getDay() === 6;
      const minutesNow = now.getHours() * 60 + now.getMinutes();
      const reminderTime = 19 * 60 + 30; // 7:30 PM

      if (!isSaturday || minutesNow < reminderTime) return;

      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const todayKey = `${yyyy}-${mm}-${dd}`;

      if (localStorage.getItem(WEEKLY_PENDING_REMINDER_KEY) === todayKey) return;

      localStorage.setItem(WEEKLY_PENDING_REMINDER_KEY, todayKey);
      toast.info("Reminder: Download pending customer balances list.");
    };

    maybeShowPendingReminder();
    const interval = setInterval(maybeShowPendingReminder, 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // DEMO: no push SW registration / external push subscription.
    if (DEMO_MODE) return;
    initPushNotifications(user).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const authStateSnapshot = captureAuthStateSnapshot();
    const sessionCycleAtStart = authSessionCycleRef.current;
    const normalizedTimeoutMinutes = normalizeSessionTimeoutMinutes(securityPolicy.session_timeout_minutes);
    const timeoutMs = normalizedTimeoutMinutes > 0 ? normalizedTimeoutMinutes * 60 * 1000 : null;
    let lastHeartbeatAt = 0;
    let retryTimer = null;
    let stopped = false;
    let failureCount = 0;
    const isCurrentSession = () =>
      !stopped &&
      sessionCycleAtStart === authSessionCycleRef.current &&
      isAuthStateSnapshotCurrent(authStateSnapshot);
    const isExpiredByInactivity = () =>
      timeoutMs != null && Date.now() - lastActivityAtRef.current >= timeoutMs;

    const scheduleRetry = () => {
      if (!isCurrentSession()) return;
      const backoffMs = Math.min(30000, 2000 * Math.max(1, failureCount));
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        pingHeartbeat();
      }, backoffMs);
    };

    const pingHeartbeat = () => {
      if (!isCurrentSession()) {
        return Promise.resolve();
      }
      if (isExpiredByInactivity()) {
        forceReloginRef.current("idle_timeout", "Session expired after inactivity. Please log in again.");
        return Promise.resolve();
      }
      logSessionTimeoutDebug("heartbeat", { failureCount });
      return (
      api.post("/auth/heartbeat", {}).then(() => {
        if (!isCurrentSession()) return;
        lastHeartbeatAt = Date.now();
        failureCount = 0;
      }).catch((err) => {
        if (!isCurrentSession()) return;
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || "");
        const detailLower = detail.toLowerCase();
        if (status === 401 || status === 403 || detailLower.includes("login disabled")) {
          const logoutReason = detailLower.includes("idle timeout")
            ? "idle_timeout"
            : detailLower.includes("absolute timeout")
              ? "absolute_timeout"
              : detailLower.includes("token")
                ? "token_expiry"
                : "session_expired";
          logSessionTimeoutDebug("heartbeat_rejected", {
            logoutReason,
            backendDetail: detail,
            status,
          });
          forceReloginRef.current(
            logoutReason,
            logoutReason === "idle_timeout"
              ? "Session expired after inactivity. Please log in again."
              : "Session expired. Please log in again."
          );
          return;
        }
        failureCount += 1;
        scheduleRetry();
      })
      );
    };

    pingHeartbeat();
    const interval = setInterval(pingHeartbeat, 60 * 1000);
    const onFocus = () => {
      if (!isCurrentSession()) return;
      if (isExpiredByInactivity()) {
        forceReloginRef.current("idle_timeout", "Session expired after inactivity. Please log in again.");
        return;
      }
      if (Date.now() - lastHeartbeatAt > 15 * 1000) {
        pingHeartbeat();
      }
    };
    const onOnline = () => {
      if (!isCurrentSession()) return;
      pingHeartbeat();
    };
    const onVisibility = () => {
      if (!isCurrentSession()) return;
      if (document.visibilityState === "visible") pingHeartbeat();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, securityPolicy.session_timeout_minutes, logSessionTimeoutDebug]);

  useEffect(() => {
    if (!user) return;
    setSecurityPolicy({
      allow_screenshots: Boolean(user?.global_allow_screenshots),
      allow_screen_share: Boolean(user?.global_allow_screen_share),
      session_timeout_minutes: normalizeSessionTimeoutMinutes(
        user?.session_timeout_minutes ?? 5
      ),
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get("/auth/security-controls")
      .then((res) => {
        if (cancelled) return;
        setSecurityPolicy((prev) => ({
          ...prev,
          allow_screenshots: Boolean(res.data?.allow_screenshots),
          allow_screen_share: Boolean(res.data?.allow_screen_share),
          session_timeout_minutes: normalizeSessionTimeoutMinutes(res.data?.session_timeout_minutes),
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!window.JEWEL_FLOW_ENV?.isElectron) return;
    if (!window.electronAPI?.setScreenshotProtection) return;
    window.electronAPI.setScreenshotProtection(!allowScreenshots).catch(() => {});
  }, [allowScreenshots]);

  useEffect(() => {
    if (!user) return;
    const allowScreenShare = Boolean(securityPolicy.allow_screen_share);
    if (window.JEWEL_FLOW_ENV?.isElectron && window.electronAPI?.setScreenShareProtection) {
      window.electronAPI.setScreenShareProtection(!allowScreenShare).catch(() => {});
    }
    const mediaDevices = navigator?.mediaDevices;
    const original = mediaDevices?.getDisplayMedia;
    if (!mediaDevices || typeof original !== "function") return;
    if (allowScreenShare) {
      const allowedWrapper = async (...args) => {
        logSecurityActivity("screen_share_started");
        return original.apply(mediaDevices, args);
      };
      mediaDevices.getDisplayMedia = allowedWrapper;
      return () => {
        mediaDevices.getDisplayMedia = original;
      };
    }

    const blocked = async () => {
      logSecurityActivity("screen_share_blocked");
      triggerCaptureShield();
      setShowScreenShareBlocked(true);
      setTimeout(() => setShowScreenShareBlocked(false), 2600);
      throw new DOMException("Screen sharing is disabled by admin", "NotAllowedError");
    };
    mediaDevices.getDisplayMedia = blocked;
    return () => {
      mediaDevices.getDisplayMedia = original;
    };
  }, [logSecurityActivity, triggerCaptureShield, user, securityPolicy.allow_screen_share]);

  useEffect(() => {
    setCurrentUser(user || null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("mmj-user-context-updated", {
          detail: { user: user || null },
        })
      );
    }
  }, [user]);

  useEffect(() => {
    const syncPolicy = (event) => {
      const detail = event?.detail || {};
      setSecurityPolicy({
        allow_screenshots: Boolean(detail.allow_screenshots),
        allow_screen_share: Boolean(detail.allow_screen_share),
        session_timeout_minutes: normalizeSessionTimeoutMinutes(detail.session_timeout_minutes),
      });
    };
    window.addEventListener("mmj-security-controls-updated", syncPolicy);
    return () => window.removeEventListener("mmj-security-controls-updated", syncPolicy);
  }, []);

  useEffect(() => {
    if (!user) return;
    lastActivityAtRef.current = Date.now();
    try {
      const synced = Number(window.localStorage.getItem(SESSION_ACTIVITY_SYNC_KEY) || 0);
      if (Number.isFinite(synced) && synced > 0) {
        lastActivityAtRef.current = Math.max(lastActivityAtRef.current, synced);
      }
      window.localStorage.setItem(SESSION_ACTIVITY_SYNC_KEY, String(lastActivityAtRef.current));
    } catch {
      // ignore storage sync failures
    }
    sessionExpiryRedirectRef.current = false;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const authStateSnapshot = captureAuthStateSnapshot();
    const sessionCycleAtStart = authSessionCycleRef.current;
    const normalizedTimeoutMinutes = normalizeSessionTimeoutMinutes(securityPolicy.session_timeout_minutes);
    if (normalizedTimeoutMinutes === 0) return undefined;
    const timeoutMs = normalizedTimeoutMinutes * 60 * 1000;
    const interval = setInterval(() => {
      if (
        sessionCycleAtStart !== authSessionCycleRef.current ||
        !isAuthStateSnapshotCurrent(authStateSnapshot)
      ) {
        return;
      }
      if (Date.now() - lastActivityAtRef.current >= timeoutMs) {
        forceReloginRef.current("idle_timeout", "Session expired after inactivity. Please log in again.");
      }
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [user, securityPolicy.session_timeout_minutes]);

  useEffect(() => {
    if (!user) return;
    const events = ["pointerdown", "keydown", "mousemove", "scroll", "touchstart", "click"];
    const handlers = new Map();
    events.forEach((eventName) => {
      const handler = () => markActivity(eventName);
      handlers.set(eventName, handler);
      window.addEventListener(eventName, handler, true);
    });
    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handlers.get(eventName), true);
      });
    };
  }, [user, markActivity]);

  useEffect(() => {
    if (!user) return;
    markActivity("navigation");
  }, [user, location.pathname, location.hash, markActivity]);

  useEffect(() => {
    if (!user) return;
    let channel = null;
    const syncIncomingActivity = (ts) => {
      const parsed = Number(ts || 0);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      lastActivityAtRef.current = Math.max(lastActivityAtRef.current, parsed);
    };
    const onStorage = (event) => {
      if (event.key !== SESSION_ACTIVITY_SYNC_KEY) return;
      syncIncomingActivity(event.newValue);
    };
    if (typeof window.BroadcastChannel === "function") {
      channel = new window.BroadcastChannel(SESSION_TIMEOUT_CHANNEL);
      activityChannelRef.current = channel;
      channel.onmessage = (event) => {
        if (event?.data?.type === "activity") {
          syncIncomingActivity(event.data.at);
        }
      };
    }
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (channel) {
        channel.close();
      }
      activityChannelRef.current = null;
    };
  }, [user]);

  useEffect(() => {
    if (!screenshotProtectionEnabled) {
      setCaptureEventActive(false);
      setCaptureHoldActive(false);
    }
  }, [logSecurityActivity, screenshotProtectionEnabled]);

  useEffect(() => {
    const cls = "mmj-capture-blur";
    document.body.classList.toggle(
      cls,
      screenshotProtectionEnabled && (captureEventActive || captureHoldActive)
    );
    return () => document.body.classList.remove(cls);
  }, [captureEventActive, captureHoldActive, screenshotProtectionEnabled]);

  useEffect(() => {
    if (!(captureEventActive || captureHoldActive)) return;
    const t = setInterval(() => setWatermarkNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [captureEventActive, captureHoldActive]);

  useEffect(() => {
    if (!screenshotProtectionEnabled) return;
    const heldKeys = new Set();
    let psReleaseTimer = null;
    const isPrintScreenKey = (e) => {
      const key = String(e?.key || "").toLowerCase();
      const code = String(e?.code || "").toLowerCase();
      const keyCode = Number(e?.keyCode || e?.which || 0);
      return key === "printscreen" || code === "printscreen" || keyCode === 44;
    };
    const isCaptureKey = (e) => {
      const key = String(e?.key || "").toLowerCase();
      return isPrintScreenKey(e) || key === "meta";
    };
    const onKeyDown = (e) => {
      if (!isCaptureKey(e)) return;
      const key = isPrintScreenKey(e) ? "printscreen" : String(e.key || "").toLowerCase();
      heldKeys.add(key);
      setCaptureHoldActive(true);
      setWatermarkNow(new Date());
      if (key === "printscreen") {
        triggerCaptureShield();
      }
    };
    const onKeyUp = (e) => {
      if (!isCaptureKey(e)) return;
      const key = isPrintScreenKey(e) ? "printscreen" : String(e.key || "").toLowerCase();
      heldKeys.delete(key);
      if (key === "printscreen") {
        if (psReleaseTimer) clearTimeout(psReleaseTimer);
        psReleaseTimer = setTimeout(() => {
          if (heldKeys.size === 0) setCaptureHoldActive(false);
        }, 1200);
      } else if (heldKeys.size === 0) {
        setCaptureHoldActive(false);
      }
    };
    const onWindowBlur = () => {
      if (screenshotProtectionEnabled) {
        // iPad/mobile screenshots often trigger blur/background transitions
        logSecurityActivity("screenshot_blocked", "Window blur/background event");
      }
      heldKeys.clear();
      setCaptureHoldActive(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    return () => {
      if (psReleaseTimer) clearTimeout(psReleaseTimer);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
    };
  }, [logSecurityActivity, screenshotProtectionEnabled, triggerCaptureShield]);

  useEffect(() => {
    if (!screenshotProtectionEnabled) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        triggerCaptureShield();
      }
    };
    const onPageHide = () => {
      logSecurityActivity("screenshot_blocked", "Page hide/background event");
      triggerCaptureShield();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [logSecurityActivity, screenshotProtectionEnabled, triggerCaptureShield]);

  const handleLogout = async () => {
    await forceReloginRef.current("manual_logout", "");
  };
  const authHintPresent = hasAuthSessionHint();

  if (backendMismatch) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-950 px-6 text-center text-white">
        <div className="max-w-lg rounded-2xl border border-red-500/40 bg-slate-900/90 p-8 shadow-[0_0_0_1px_rgba(239,68,68,0.18),0_16px_48px_rgba(0,0,0,0.5)]">
          <h1 className="text-xl font-bold text-red-400 mb-3">Backend Mismatch — App Halted</h1>
          <p className="text-sm text-slate-300 mb-4">
            The backend is not running the <strong>JewelFlow SQLCipher Local Backend</strong>.
            A legacy or MongoDB backend may be active.
            <br /><br />
            <code className="text-red-300 text-xs">/api/local-security/status</code> returned 404.
          </p>
          <p className="text-xs text-slate-500 mb-6">
            Do not use the app until the correct backend is confirmed.
            Restart the launcher and verify the startup log shows:
            <br /><span className="text-emerald-400">SQLCipher backend loaded: app.sqlcipher_app</span>
          </p>
          <button
            className="rounded-lg bg-red-700 hover:bg-red-600 px-5 py-2 text-sm font-medium text-white transition"
            onClick={() => window.location.reload()}
          >
            Reload (after fixing backend)
          </button>
        </div>
      </div>
    );
  }

  if (isElectronRuntime && loadingRuntimeOwnership) {
    return <AuthPendingScreen />;
  }

  if (
    isElectronRuntime &&
    runtimeOwnership &&
    !runtimeOwnership.allow_local_hosting &&
    !user &&
    location.pathname !== "/vault-unlock"
  ) {
    const isUninitialized = runtimeOwnership.business_mode === "primary_not_configured";
    const isMismatch = runtimeOwnership.business_mode === "ownership_mismatch";
    const isClient = runtimeOwnership.business_mode === "client_device";
    const isRevoked = runtimeOwnership.business_mode === "revoked_primary_device";
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-950 px-6 text-center text-white">
        <div className="max-w-xl rounded-2xl border border-emerald-500/30 bg-slate-900/90 p-8 shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_16px_48px_rgba(0,0,0,0.5)]">
          <h1 className="text-xl font-bold text-emerald-300 mb-3">
            {isUninitialized
              ? "Primary device not configured"
              : isMismatch
              ? "Ownership mismatch"
              : isRevoked
              ? "Primary device revoked"
              : isClient
              ? "Client device mode"
              : "Runtime ownership status"}
          </h1>
          <p className="text-sm text-slate-200 mb-4">
            {runtimeOwnership.display_message || "This device is not allowed to run local JewelFlow host services."}
          </p>
          <div className="rounded-xl border border-slate-700 bg-slate-950/70 p-4 text-left text-sm text-slate-300 space-y-2 mb-6">
            <div><strong className="text-slate-100">Business mode:</strong> {String(runtimeOwnership.business_mode || "").replace(/_/g, " ")}</div>
            <div><strong className="text-slate-100">Active business slug:</strong> {runtimeOwnership?.drift?.electron_active_business_slug || "—"}</div>
            <div><strong className="text-slate-100">Backend active business:</strong> {runtimeOwnership?.backend_active_business_id || runtimeOwnership?.drift?.backend_active_business_id || "—"}</div>
            <div><strong className="text-slate-100">Primary device:</strong> {runtimeOwnership?.runtime_ownership?.primary_device_name || "Not configured"}</div>
            <div><strong className="text-slate-100">Primary last seen:</strong> {runtimeOwnership?.runtime_ownership?.primary_device_last_seen || "—"}</div>
          </div>
          {(runtimeOwnership?.runtime_ownership?.previous_primary_device_name ||
            runtimeOwnership?.runtime_ownership?.ownership_transferred_at) ? (
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-left text-sm text-slate-300 space-y-2 mb-6">
              {runtimeOwnership?.runtime_ownership?.previous_primary_device_name ? (
                <div><strong className="text-slate-100">Previous primary:</strong> {runtimeOwnership.runtime_ownership.previous_primary_device_name}</div>
              ) : null}
              {runtimeOwnership?.runtime_ownership?.ownership_transferred_at ? (
                <div><strong className="text-slate-100">Transferred at:</strong> {runtimeOwnership.runtime_ownership.ownership_transferred_at}</div>
              ) : null}
            </div>
          ) : null}
          {isUninitialized ? (
            <div className="space-y-4 text-left">
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">
                {runtimeOwnership.claim_warning ||
                  "This device will become the main JewelFlow server for this business. It will run the backend, local gateway, and remote access tunnel."}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2" htmlFor="primary-vault-password">
                  Business Vault Password
                </label>
                <input
                  id="primary-vault-password"
                  type="password"
                  value={primaryVaultPassword}
                  onChange={(e) => setPrimaryVaultPassword(e.target.value)}
                  className="w-full rounded-lg border border-slate-600 bg-slate-950/80 px-3 py-2 text-white"
                  autoComplete="current-password"
                />
              </div>
              <button
                className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 px-5 py-2 text-sm font-medium text-white transition disabled:opacity-60"
                disabled={claimingPrimary || !primaryVaultPassword.trim()}
                onClick={async () => {
                  try {
                    setClaimingPrimary(true);
                    const res = await window.electronAPI?.claimPrimaryOwnership(primaryVaultPassword);
                    if (!res?.ok) {
                      throw new Error(res?.error || "Failed to set this device as Primary.");
                    }
                    setRuntimeOwnership(res.ownership || null);
                    setPrimaryVaultPassword("");
                    toast.success("This device is now the Primary Device for this business.");
                    window.setTimeout(() => window.location.reload(), 900);
                  } catch (err) {
                    toast.error(err?.message || "Failed to set this device as Primary.");
                  } finally {
                    setClaimingPrimary(false);
                  }
                }}
              >
                {claimingPrimary ? "Verifying..." : "Set this device as Primary"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {isRevoked ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-left text-sm text-rose-100">
                  This laptop is no longer the main JewelFlow server for this business. Host startup stays blocked here.
                  {runtimeOwnership?.runtime_ownership?.primary_device_name ? ` Use ${runtimeOwnership.runtime_ownership.primary_device_name} as the active Primary Device.` : ""}
                </div>
              ) : null}
              <button
                className="rounded-lg bg-slate-700 hover:bg-slate-600 px-5 py-2 text-sm font-medium text-white transition"
                onClick={() => window.location.reload()}
              >
                Reload Status
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loadingUser) {
    return <AuthPendingScreen />;
  }

  if (remoteServerOffline && !user && location.pathname !== "/vault-unlock") {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-950 px-6 text-center text-white">
        <div className="max-w-lg rounded-2xl border border-amber-500/40 bg-slate-900/90 p-8 shadow-[0_0_0_1px_rgba(245,158,11,0.18),0_16px_48px_rgba(0,0,0,0.5)]">
          <h1 className="text-xl font-bold text-amber-300 mb-3">Remote Access Offline</h1>
          <p className="text-sm text-slate-200 mb-6">
            Shop server is offline. Turn on the main JewelFlow laptop/server to access live app data.
          </p>
          <button
            className="rounded-lg bg-amber-600 hover:bg-amber-500 px-5 py-2 text-sm font-medium text-slate-950 transition"
            onClick={() => window.location.reload()}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const watermarkText = `${String(user?.full_name || "User")} • ${String(user?.role || "user").toUpperCase()} • ${watermarkNow.toLocaleString()}`;
  return (
    <AppErrorBoundary>
      <BrandingProvider>
        <PrintPreferencesProvider>
        <LanguageProvider>
        <PrivacyProvider>
          <Toaster position="top-center" richColors />
          {DEMO_MODE && <DemoBanner />}
          {DEMO_MODE && <DemoWatermark />}
          {location.pathname !== "/login" && location.pathname !== "/vault-unlock" && (
            <Suspense fallback={null}>
              <GlobalAssistant />
            </Suspense>
          )}
          <div id="mmj-ui-root">
          {screenshotProtectionEnabled && (captureEventActive || captureHoldActive) && (
            <>
              <div className="mmj-capture-shield" aria-hidden="true" />
              <div className="mmj-capture-watermark" aria-hidden="true">
                <div className="mmj-capture-watermark-center">{watermarkText}</div>
              </div>
            </>
          )}
          {showScreenShareBlocked && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/95">
              <div className="rounded-2xl border border-white/15 bg-black/70 px-8 py-7 text-center text-white shadow-2xl">
                <div className="mx-auto mb-4 h-20 w-20 relative flex items-center justify-center">
                  <EyeOff className="h-16 w-16 text-white/95" />
                  <span className="mmj-eye-strike" />
                </div>
                <p className="text-xl font-semibold">Stop screen sharing</p>
                <p className="mt-1 text-sm text-white/70">
                  Screen sharing is blocked by admin security controls.
                </p>
              </div>
            </div>
          )}

          <Suspense
            fallback={
              <div className="w-full min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
                Loading page...
              </div>
            }
          >
          <Routes key={DEMO_MODE ? demoEpoch : undefined}>
            <Route
              path="/login"
              element={
                <Login
                  setUser={setUser}
                />
              }
            />
            <Route path="/vault-unlock" element={<VaultUnlock />} />

            <Route
              path="/"
              element={
                <SectionRoute user={user} onLogout={handleLogout} sectionPath="/" loadingUser={loadingUser} hasAuthHint={authHintPresent}>
                  <Dashboard />
                </SectionRoute>
              }
            />

            <Route path="/customers" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/customers" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Customers /></SectionRoute>} />
            <Route path="/invoices" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/invoices" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Invoices /></SectionRoute>} />
            <Route path="/approvals" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/invoices" loadingUser={loadingUser} hasAuthHint={authHintPresent}><CustomerRelation /></SectionRoute>} />
            <Route path="/invoice/:id" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/invoices" loadingUser={loadingUser} hasAuthHint={authHintPresent}><InvoiceView /></SectionRoute>} />
            <Route path="/items" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/items" loadingUser={loadingUser} hasAuthHint={authHintPresent}><ItemMaster /></SectionRoute>} />
            <Route path="/settings/gold-rate" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/settings/gold-rate" loadingUser={loadingUser} hasAuthHint={authHintPresent}><GoldRateSettings /></SectionRoute>} />
            <Route path="/repairs" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/repairs" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Repairs /></SectionRoute>} />
            <Route path="/karigars" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/karigars" loadingUser={loadingUser} hasAuthHint={authHintPresent}><KarigarManagement /></SectionRoute>} />
            <Route path="/reports" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/reports" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Reports showMarginsInReports={false} /></SectionRoute>} />
            <Route path="/reports/stock-details" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/reports" loadingUser={loadingUser} hasAuthHint={authHintPresent}><StockDetails /></SectionRoute>} />
            <Route path="/item-margins" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/items" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Reports forcedMainTab="margins" hideMainTabs /></SectionRoute>} />
            <Route path="/customer-balances" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/customer-balances" loadingUser={loadingUser} hasAuthHint={authHintPresent}><CustomerBalanceManagement /></SectionRoute>} />
            <Route path="/supplier-balances" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/supplier-balances" loadingUser={loadingUser} hasAuthHint={authHintPresent}><SupplierBalance /></SectionRoute>} />
            <Route path="/expenses" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/purchases" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Expenses /></SectionRoute>} />
            <Route path="/purchases" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/purchases" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Purchases /></SectionRoute>} />
            <Route path="/purchase/:id" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/purchases" loadingUser={loadingUser} hasAuthHint={authHintPresent}><PurchaseView /></SectionRoute>} />
            <Route path="/purchase-returns" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/purchases" loadingUser={loadingUser} hasAuthHint={authHintPresent}><PurchaseReturns /></SectionRoute>} />
            <Route path="/consignment" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/purchases" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Consignment user={user} onLogout={handleLogout} /></SectionRoute>} />
            <Route path="/customer-balance-reports" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/customer-balances" loadingUser={loadingUser} hasAuthHint={authHintPresent}><CustomerBalanceReports /></SectionRoute>} />
            <Route path="/settings" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/settings" loadingUser={loadingUser} hasAuthHint={authHintPresent}><Settings /></SectionRoute>} />
            <Route path="/settings/runtime-diagnostics" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/settings" loadingUser={loadingUser} hasAuthHint={authHintPresent}><RuntimeDiagnostics /></SectionRoute>} />
            <Route path="/customer-relation" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/customer-relation" loadingUser={loadingUser} hasAuthHint={authHintPresent}><CustomerRelation /></SectionRoute>} />
            <Route path="/storage" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/storage" loadingUser={loadingUser} hasAuthHint={authHintPresent}><StorageUnits /></SectionRoute>} />
            <Route path="/bulk-inventory" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/bulk-inventory" loadingUser={loadingUser} hasAuthHint={authHintPresent}><BulkInventory /></SectionRoute>} />
            <Route path="/verification" element={<SectionRoute user={user} onLogout={handleLogout} sectionPath="/verification" loadingUser={loadingUser} hasAuthHint={authHintPresent}><StockVerification /></SectionRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </div>
        </PrivacyProvider>
        </LanguageProvider>
        </PrintPreferencesProvider>
      </BrandingProvider>
    </AppErrorBoundary>
  );
}

export default App;
